import React, { useEffect, useRef } from "react";
import { Typography, Container, Box } from "@mui/material";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const categories = [
  "1st Year Only",
  "Masters Only",
  "Combined 2nd, 3rd, and 4th Year",
];

export default function EventDetails() {
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const categoriesRef = useRef(null);
  const descriptionRef = useRef(null);

  useEffect(() => {
    // Fade in and scale up the entire container
    gsap.from(containerRef.current, {
      opacity: 0,
      scale: 0.9,
      duration: 1.2,
      ease: "power2.out",
    });

    // Scroll-triggered animation for the title
    gsap.from(titleRef.current, {
      opacity: 0,
      y: -50,
      duration: 1,
      scrollTrigger: {
        trigger: titleRef.current,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    // Animate categories with stagger effect
    gsap.from(categoriesRef.current.children, {
      opacity: 0,
      x: -50,
      stagger: 0.3,
      duration: 1,
      scrollTrigger: {
        trigger: categoriesRef.current,
        start: "top 90%",
        toggleActions: "play none none none",
      },
    });

    // Description animation
    gsap.from(descriptionRef.current, {
      opacity: 0,
      y: 50,
      duration: 1,
      scrollTrigger: {
        trigger: descriptionRef.current,
        start: "top 85%",
        toggleActions: "play none none none",
      },
    });
  }, []);

  useEffect(() => {
    const tl = gsap.timeline({ repeat: -1, repeatDelay: 1 });

    tl.to(".first", {
      y: "-100%",
      duration: 1,
      opacity: 0,
      ease: "expo.inOut",
    })
      .to(
        ".second",
        {
          y: "0%",
          duration: 1,
          opacity: 1,
          ease: "expo.inOut",
        },
        "-=0.5"
      )
      .to(".second", {
        y: "-100%",
        duration: 1,
        opacity: 0,
        ease: "expo.inOut",
      })
      .to(
        ".third",
        {
          y: "0%",
          duration: 1,
          opacity: 1,
          ease: "expo.inOut",
        },
        "-=0.5"
      )
      .to(".third", {
        y: "-100%",
        duration: 1,
        opacity: 0,
        ease: "expo.inOut",
      })
      .set(".first", { y: "100%", opacity: 0 }) // Reset first for loop
      .to(".first", {
        y: "0%",
        duration: 0.5,
        opacity: 1,
        ease: "expo.inOut",
      });
  }, []);

  return (
    <div style={{ margin: "12px" }}>
      <Container
        ref={containerRef}
        sx={{
          bgcolor: "rgba(0, 0, 0, 0.15)",
          color: "white",
          px: 3,
          borderRadius: "20px",
          py: "20px",
          boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px"
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 4,
            position: "relative",
            overflow: "hidden",
            height: "74px", // Set a fixed height to avoid layout shift
          }}
        >
          <Typography
            className="first"
            align="center"
            fontWeight={700}
            color="#023047"
            bgcolor="white"
            sx={{
              py: 1,
              px: 2,
              borderRadius: "20px",
              position: "absolute",
              fontSize: {
                xs: "5vw",
                sm: "5vw",
                md: "3vw",
                lg: "3vw",
                xl: "2vw",
              },
            }}
          >
            Event Details
          </Typography>
          <Typography
            className="second"
            align="center"
            fontWeight={700}
            color="green"
            bgcolor="white"
            sx={{
              py: 1,
              px: 2,
              borderRadius: "20px",
              position: "absolute",
              transform: "translateY(100%)",
              opacity: 0,
              fontSize: {
                xs: "5vw",
                sm: "5vw",
                md: "3vw",
                lg: "3vw",
                xl: "2vw",
              },
            }}
          >
            ACCEPTED
          </Typography>
          <Typography
            className="third"
            align="center"
            fontWeight={700}
            color="red"
            bgcolor="white"
            sx={{
              py: 1,
              px: 2,
              borderRadius: "20px",
              position: "absolute",
              transform: "translateY(200%)",
              opacity: 0,
              fontSize: {
                xs: "5vw",
                sm: "5vw",
                md: "3vw",
                lg: "3vw",
                xl: "2vw",
              },
            }}
          >
            Wrong Answer Test Case 2
          </Typography>
        </Box>

        <div>
          <Typography variant="h4" sx={{ mt: 5, mb: 3 }}>
            Categories
          </Typography>
          <ul ref={categoriesRef}>
            {categories.map((category, index) => (
              <li key={index}>
                <Typography variant="h6">{category}</Typography>
              </li>
            ))}
          </ul>
        </div>

        <Typography variant="h4" sx={{ mt: 5, mb: 3 }}>
          Description
        </Typography>
        <Typography ref={descriptionRef} variant="body1" sx={{ mt: 2, mb: 5 }}>
          1v1 Coding Contest where you will be given multiple problem
          statements, and you must solve them within a given time frame. The
          participant who solves the problems in the shortest time wins. The
          contest will be held on Codeforces, and the link to the contest will
          be shared with you on the day of the event.
        </Typography>
      </Container>
    </div>
  );
}
